import { NameHelpers } from '@principle-theorem/principle-core';
import {
  type DepositRequestDeliveryChannel,
  type IAppointment,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import { snapshot, toMoment, type WithRef } from '@principle-theorem/shared';
import { filter } from 'rxjs/operators';
import {
  type DepositStore,
  type ISendPaymentLinkParams,
} from './deposit.store';

export interface ISavedAppointmentPaymentLinkContext {
  appointment: WithRef<IAppointment>;
  patient: WithRef<IPatient>;
  deliveryChannel: DepositRequestDeliveryChannel;
  requestKey: string;
}

/**
 * Builds the payload for a deposit link against an appointment that already
 * exists. Sending `appointmentRef` (rather than the pre-save `event` +
 * `templateId` + `requestKey` trio) is what routes the server away from the
 * provisional blocking-request branch and onto the existing-appointment one.
 */
export function buildSavedAppointmentPaymentLinkParams(
  context: ISavedAppointmentPaymentLinkContext
): ISendPaymentLinkParams | undefined {
  const { appointment, patient } = context;
  if (!appointment.deposit) {
    return;
  }

  const eventMoment = appointment.event
    ? toMoment(appointment.event.from)
    : undefined;

  return {
    practiceRef: appointment.practice.ref,
    appointmentRef: appointment.ref,
    patientRef: patient.ref,
    treatmentName: appointment.treatmentPlan.treatmentStep.name,
    amountCents: appointment.deposit.metadata.amountCents,
    paymentMode: appointment.deposit.metadata.paymentMode,
    captureStrategy: appointment.deposit.metadata.captureStrategy,
    deliveryChannel: context.deliveryChannel,
    appointmentDate: eventMoment?.format('DD/MM/YYYY') ?? '',
    appointmentTime: eventMoment?.format('h:mm A') ?? '',
    patientEmail: patient.email ?? '',
    patientFirstName: NameHelpers.firstName(
      NameHelpers.withoutTitle(patient.name)
    ),
    patientName: patient.name,
    practiceName: appointment.practice.name,
    requestKey: context.requestKey,
  };
}

/**
 * Resolves once the store has settled the in-flight send. Returns false when
 * the link could not be created or delivered, so the deposit is never flipped
 * to Pending off the back of a failed delivery. The error itself is surfaced by
 * the caller's `paymentLinkError$` subscription, which is what keeps failures to
 * exactly one snackbar.
 */
export async function paymentLinkDelivered(
  depositStore: DepositStore
): Promise<boolean> {
  await snapshot(
    depositStore.paymentLinkSending$.pipe(filter((sending) => !sending))
  );
  const error = await snapshot(depositStore.paymentLinkError$);
  if (error) {
    return false;
  }
  const depositRequest = await snapshot(depositStore.depositRequest$);
  return depositRequest !== undefined;
}
